'use client';

import React, { useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { QuickCalculatorForm } from '@/components/quick-calculator/QuickCalculatorForm';
import { calculateSadeSati, SadeSatiResult, CreateBirthProfileInput } from '@/lib/api/client';
import { ShieldCheck, Clock, Compass, Loader2 } from 'lucide-react';
import { formatLocalizedDate } from '@/lib/i18n/dateFormat';

export interface SadeSatiCalculatorClientProps {
  initialResult?: SadeSatiResult | null;
  initialError?: string | null;
}

export function SadeSatiCalculatorClient({
  initialResult = null,
  initialError = null
}: SadeSatiCalculatorClientProps) {
  const t = useTranslations('sadeSati');
  const tGlossary = useTranslations('glossary');
  const locale = useLocale();

  const [result, setResult] = useState<SadeSatiResult | null>(initialResult);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);

  const handleCalculate = async (input: CreateBirthProfileInput) => {
    setLoading(true);
    setError(null);
    try {
      const data = await calculateSadeSati(input);
      setResult(data);
    } catch (err: any) {
      setError(err.message || t('noActivePhase'));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const getSignName = (signKey?: string | null): string => {
    if (!signKey) return '';
    const key = signKey.toLowerCase();
    try {
      const val = tGlossary(`zodiacSigns.${key}` as any);
      if (val && !val.startsWith('zodiacSigns.')) return val;
    } catch (_) {}
    return signKey;
  };

  const getPhaseName = (phaseStr?: string | null): string => {
    if (!phaseStr) return '';
    const p = phaseStr.toLowerCase();
    if (p.includes('1') || p.includes('first')) {
      try {
        const val = tGlossary('sadeSatiPhases.phase1');
        if (val && !val.startsWith('sadeSatiPhases.')) return val;
      } catch (_) {}
    }
    if (p.includes('2') || p.includes('second') || p.includes('peak')) {
      try {
        const val = tGlossary('sadeSatiPhases.phase2');
        if (val && !val.startsWith('sadeSatiPhases.')) return val;
      } catch (_) {}
    }
    if (p.includes('3') || p.includes('third') || p.includes('setting')) {
      try {
        const val = tGlossary('sadeSatiPhases.phase3');
        if (val && !val.startsWith('sadeSatiPhases.')) return val;
      } catch (_) {}
    }
    if (p.includes('dhaiya') || p.includes('dhayya') || p.includes('kantak') || p.includes('ashtam')) {
      try {
        const val = tGlossary('sadeSatiPhases.dhaiya');
        if (val && !val.startsWith('sadeSatiPhases.')) return val;
      } catch (_) {}
    }
    return phaseStr;
  };

  const getResultStatusTitle = (res: SadeSatiResult): string => {
    if (res.is_sade_sati_active) {
      const phase = getPhaseName(res.current_phase);
      return t('activePhase', { phase });
    }
    return t('noActivePhase');
  };

  const formatApproxDate = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    try {
      return formatLocalizedDate(dateStr, locale);
    } catch (_) {
      return dateStr;
    }
  };

  return (
    <div className="space-y-8" data-testid="sade-sati-client">
      <QuickCalculatorForm
        title={t('title')}
        subtitle={t('subtitle')}
        onCalculate={handleCalculate}
        loading={loading}
      />

      {loading && (
        <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-8 text-center space-y-3 shadow-xs">
          <Loader2 className="w-8 h-8 text-[var(--gold)] animate-spin mx-auto" />
          <h3 className="text-base font-serif font-medium text-[var(--foreground)]">{t('steppingNoticeTitle')}</h3>
          <p className="text-xs text-[var(--muted)] max-w-md mx-auto">
            {t('steppingNoticeDesc')}
          </p>
        </div>
      )}

      {error && (
        <div data-testid="sade-sati-error-panel" className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 text-rose-500 text-sm">
          {error}
        </div>
      )}

      {result && !loading && (
        <div data-testid="sade-sati-result-panel" className="space-y-6">
          {/* Main Status Header Card */}
          <div
            className={`border rounded-2xl p-6 sm:p-8 shadow-xs relative overflow-hidden text-center sm:text-left ${
              result.is_sade_sati_active
                ? 'bg-purple-500/10 border-purple-500/40'
                : 'bg-emerald-500/10 border-emerald-500/40'
            }`}
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div
                  className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                    result.is_sade_sati_active
                      ? 'bg-purple-500/20 text-purple-400'
                      : 'bg-emerald-500/20 text-emerald-400'
                  }`}
                >
                  {result.is_sade_sati_active ? (
                    <Clock className="w-8 h-8" />
                  ) : (
                    <ShieldCheck className="w-8 h-8" />
                  )}
                </div>

                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider block text-[var(--muted)]">
                    {t('resultHeading')}
                  </span>
                  <h2 className="text-2xl font-serif font-medium text-[var(--foreground)] mt-0.5">
                    {getResultStatusTitle(result)}
                  </h2>
                  <span className="text-xs font-mono text-[var(--gold)] mt-1 block">
                    {t('signsSubtitle', {
                      moonSign: getSignName(result.natal_moon_sign),
                      saturnSign: getSignName(result.transiting_saturn_sign)
                    })}
                  </span>
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-full border text-xs font-bold uppercase tracking-wider ${
                  result.is_sade_sati_active
                    ? 'bg-purple-500/20 text-purple-400 border-purple-500/40'
                    : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                }`}
              >
                {result.is_sade_sati_active ? t('badgeActive') : t('badgeClear')}
              </div>
            </div>

            {result.is_sade_sati_active ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <div className="bg-[var(--background)] border border-[var(--line)] rounded-xl p-4 space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--gold)] block">
                    {t('phaseWindowLabel')}
                  </span>
                  <div className="text-sm font-mono font-bold text-[var(--foreground)]">
                    {formatApproxDate(result.phase_start_date)} <span className="text-[var(--muted)]">{t('toWord')}</span> {formatApproxDate(result.phase_end_date)}
                  </div>
                </div>

                <div className="bg-[var(--background)] border border-[var(--line)] rounded-xl p-4 space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400 block">
                    {t('cycleWindowLabel')}
                  </span>
                  <div className="text-sm font-mono font-bold text-[var(--foreground)]">
                    {formatApproxDate(result.overall_cycle_start_date)} <span className="text-[var(--muted)]">{t('toWord')}</span> {formatApproxDate(result.overall_cycle_end_date)}
                  </div>
                </div>
              </div>
            ) : result.next_onset_approx_year ? (
              <div className="mt-4 bg-[var(--background)] border border-[var(--line)] rounded-xl p-4 flex items-center justify-between text-xs text-[var(--muted)]">
                <span>{t('nextOnsetLabel')}</span>
                <span className="font-mono font-bold text-[var(--gold)] text-sm">
                  {t('nextOnsetApprox', { year: result.next_onset_approx_year })}
                </span>
              </div>
            ) : null}
          </div>

          {/* Measured Tone & Astrological Perspective */}
          <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 shadow-xs space-y-3">
            <h3 className="text-base font-serif font-medium text-[var(--foreground)] flex items-center gap-2">
              <Compass className="w-4 h-4 text-[var(--gold)]" />
              <span>{t('perspectiveTitle')}</span>
            </h3>
            <p className="text-xs text-[var(--muted)] leading-relaxed">
              {t('perspectiveBody')}
            </p>
            <div className="bg-[var(--background)] border border-[var(--line)] rounded-xl p-3.5 text-xs text-[var(--muted)] italic">
              {t('perspectiveNote')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
